import React from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckBadgeIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  CogIcon,
  ChartBarIcon,
  SaveIcon,
    VideoCameraIcon,

  EyeIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';

export default function TaskCalendarHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <CalendarIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Task Calendar System</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Interactive task scheduling and assignment system with visual calendar interface,
            automated break management, and comprehensive workflow tracking.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Task Calendar Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Weekly Calendar</h3>
                <p className="text-gray-600">
                  Visual weekly view with shift-based time slots for task assignment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Staff Assignment</h3>
                <p className="text-gray-600">
                  Select staff members and assign specific tasks to their scheduled shifts.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Break Management</h3>
                <p className="text-gray-600">
                  Automated break scheduling for shifts 6+ hours with flexible task assignment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Tracking</h3>
                <p className="text-gray-600">
                  Visual indicators for task completion and missing assignments.
                </p>
              </div>
            </div>

            {/* Static Calendar Demo */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Interactive Calendar Interface</h3>

              {/* Static Header with Labels */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-8">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                        <span className="text-white font-semibold">October 2025</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded p-1 transition-colors">
                          <ChevronLeftIcon className="h-5 w-5 text-white" />
                        </button>
                        <div className="bg-white bg-opacity-20 rounded px-3 py-1">
                          <span className="text-white font-medium">14 Oct 2025</span>
                        </div>
                        <button className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded p-1 transition-colors">
                          <ChevronRightIcon className="h-5 w-5 text-white" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <select className="bg-white bg-opacity-20 text-white rounded px-3 py-1 border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50">
                        <option className="text-gray-900">Select Staff Member</option>
                        <option className="text-gray-900">John Smith - Manager</option>
                        <option className="text-gray-900">Sarah Johnson - Staff</option>
                      </select>

                      <div className="flex space-x-2">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                          Save Tasks
                        </button>
                        <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                          Task Graph
                        </button>
                        <button className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                          Processes
                        </button>
                        <button className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                          Tasks
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="bg-green-50 border-b border-green-200 px-6 py-4">
                  <div className="flex items-center text-green-700">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">All Tasks Assigned For This Week</span>
                  </div>
                </div>

                {/* Available Tasks Section */}
                <div className="p-6 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Available Tasks</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-r from-red-500 to-orange-500 p-3 rounded-lg text-white">
                      <div className="flex items-center">
                        <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">1</span>
                        <span className="font-medium">Customer Service</span>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-lg text-white">
                      <div className="flex items-center">
                        <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">2</span>
                        <span className="font-medium">Inventory Check</span>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-lg text-white">
                      <div className="flex items-center">
                        <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">3</span>
                        <span className="font-medium">Cash Handling</span>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-500 to-amber-600 p-3 rounded-lg text-white">
                      <div className="flex items-center">
                        <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">N/A</span>
                        <span className="font-medium">Break</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calendar Grid with Labels */}
                <div className="relative">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 border-b border-gray-200">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                      <div key={day} className="p-4 text-center border-r border-gray-200 last:border-r-0">
                        <div className="font-semibold text-gray-900">{day}</div>
                        <div className="text-2xl font-bold text-indigo-600 mt-1">{14 + index}</div>
                      </div>
                    ))}
                  </div>

                  {/* Time Slots with Interactive Elements */}
                  <div className="grid grid-cols-7">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                      <div key={day} className="border-r border-gray-200 last:border-r-0 p-4 space-y-3">
                        {/* Break Indicator */}
                        {index === 2 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
                            <div className="text-xs text-yellow-700 text-center">
                              <div className="font-medium">Legal Break Required</div>
                              <div>30 minutes</div>
                            </div>
                          </div>
                        )}

                        {/* Time Slots */}
                        {[9, 10, 11, 12, 13, 14, 15, 16, 17].map((hour) => (
                          <div key={hour} className="relative">
                            {/* Time Label */}
                            <div className="text-xs font-semibold text-gray-500 mb-1 text-center">
                              {hour}:00
                            </div>

                            {/* Task Slot */}
                            <div className={`border-l-4 rounded-md p-2 cursor-pointer transition-all hover:shadow-md ${
                              index === 1 && hour === 10 ? 'bg-blue-50 border-blue-500' :
                              index === 2 && hour === 14 ? 'bg-red-50 border-red-500' :
                              index === 3 && hour === 11 ? 'bg-green-50 border-green-500' :
                              'bg-gray-50 border-gray-300'
                            }`}>
                              <div className={`text-sm font-medium ${
                                index === 1 && hour === 10 ? 'text-blue-700' :
                                index === 2 && hour === 14 ? 'text-red-700' :
                                index === 3 && hour === 11 ? 'text-green-700' :
                                'text-gray-500'
                              }`}>
                                {index === 1 && hour === 10 ? 'Customer Service (Step 1)' :
                                 index === 2 && hour === 14 ? 'Break (12:00-12:30) + Inventory' :
                                 index === 3 && hour === 11 ? 'Cash Handling (Step 2)' :
                                 'Select Task'}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex justify-center mt-1 space-x-1">
                                {(index === 1 && hour === 10) && (
                                  <button className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full hover:bg-indigo-200 transition-colors">
                                    View Previous
                                  </button>
                                )}
                                {(index === 2 && hour === 14) && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                    Missing Task
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Interface Explanations */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <h4 className="text-lg font-bold text-blue-900">Week Navigation</h4>
                  </div>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Navigate between weeks using the arrow buttons. Click the date button to jump to the current week.
                    The month/year display shows the selected week's time period.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <h4 className="text-lg font-bold text-yellow-900">Staff Selection</h4>
                  </div>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    Choose a staff member from the dropdown to view their scheduled shifts and assign tasks.
                    The system loads their specific shift schedule and available tasks.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <h4 className="text-lg font-bold text-purple-900">Action Buttons</h4>
                  </div>
                  <p className="text-purple-700 text-sm leading-relaxed">
                    Save your task assignments, view performance graphs, access process definitions,
                    and review team task assignments with dedicated tools.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-green-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    <h4 className="text-lg font-bold text-green-900">Status Indicator</h4>
                  </div>
                  <p className="text-green-700 text-sm leading-relaxed">
                    Real-time status showing task assignment completion. Green indicates all tasks are assigned,
                    red shows missing assignments, amber indicates partial completion.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">5</span>
                    </div>
                    <h4 className="text-lg font-bold text-indigo-900">Available Tasks</h4>
                  </div>
                  <p className="text-indigo-700 text-sm leading-relaxed">
                    Process steps and tasks organized by business workflows. Each task shows its step number,
                    process name, and is color-coded for easy identification.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">6</span>
                    </div>
                    <h4 className="text-lg font-bold text-pink-900">Time Slots</h4>
                  </div>
                  <p className="text-pink-700 text-sm leading-relaxed">
                    Hourly time slots based on staff shift schedules. Click any slot to assign tasks.
                    Color-coded borders show assigned tasks, gray indicates available slots.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">7</span>
                    </div>
                    <h4 className="text-lg font-bold text-orange-900">Break Management</h4>
                  </div>
                  <p className="text-orange-700 text-sm leading-relaxed">
                    Automated break scheduling for shifts 6+ hours. Shows required break duration
                    and allows additional task assignment during short breaks.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">8</span>
                    </div>
                    <h4 className="text-lg font-bold text-teal-900">Task Assignment</h4>
                  </div>
                  <p className="text-teal-700 text-sm leading-relaxed">
                    Visual feedback for task assignments. "View Previous" button shows historical task data,
                    red indicators highlight missing assignments that need attention.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works - Step by Step */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">How Task Calendar Works</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Select Staff Member</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Choose a staff member from the dropdown to view their scheduled shifts and assign tasks.
                  </p>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Staff Selection Process</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Dropdown shows all active staff members</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Displays name and role for easy identification</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Automatically loads their shift schedule</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Fetches available tasks for their role</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-blue-900 mb-2">Example Selection</h5>
                        <div className="text-sm text-blue-700">
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>Selected:</strong> Sarah Johnson - Staff
                          </div>
                          <div className="text-xs text-gray-600">
                            This loads Sarah's shifts for the current week and shows tasks she can perform.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Review Available Tasks</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    View all tasks and process steps available for assignment, organized by process.
                  </p>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">Task Organization</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Grouped by business processes</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Step numbers for sequential tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Color-coded by process type</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Break task always available</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Task Card Example</h5>
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-3 rounded-lg text-white text-sm">
                          <div className="flex items-center">
                            <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">1</span>
                            <div>
                              <div className="font-medium">Customer Service</div>
                              <div className="text-xs opacity-90">Sales Process</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Break Task</h5>
                        <div className="bg-gradient-to-r from-yellow-500 to-amber-600 p-3 rounded-lg text-white text-sm">
                          <div className="flex items-center">
                            <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-sm font-bold">N/A</span>
                            <div>
                              <div className="font-medium">Break</div>
                              <div className="text-xs opacity-90">Rest Period</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Assign Tasks to Time Slots</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Click on time slots to assign tasks, with special handling for breaks and missing assignments.
                  </p>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Task Assignment Process</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Click any time slot to open task selector</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Modal overlay shows all available tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Color-coded tasks by process type</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Visual feedback for assigned tasks</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Special Features</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Break scheduling for 6+ hour shifts</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Additional tasks during short breaks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Missing task indicators</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Previous task history access</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-pink-500 to-red-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Save and Review Assignments</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Save your task assignments and use additional tools to review processes and performance.
                  </p>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Save Process</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Click "Save Tasks" to store assignments</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Server-side validation and storage</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Success confirmation message</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Automatic data refresh</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Additional Tools</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Task Graph - Visual performance metrics</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Processes - View workflow definitions</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Tasks - Team task overview</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Status indicators - Completion tracking</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-pink-200">
                        <h5 className="font-semibold text-pink-900 mb-2">Status Messages</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-green-700">
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            All Tasks Assigned For This Week
                          </div>
                          <div className="flex items-center text-red-700">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                            No Shifts Booked For This Week
                          </div>
                          <div className="flex items-center text-yellow-700">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                            Some timeslots are missing task assignments
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Capabilities</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Dynamic Calendar</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Week-by-week navigation</li>
                  <li>• Real-time shift loading</li>
                  <li>• Mobile-responsive design</li>
                  <li>• Date-based task storage</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Time Slot Management</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Hourly time slot generation</li>
                  <li>• Shift-based availability</li>
                  <li>• Break time integration</li>
                  <li>• Missing task detection</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <UserGroupIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Staff Integration</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Role-based task filtering</li>
                  <li>• Staff shift synchronization</li>
                  <li>• Individual task history</li>
                  <li>• Performance tracking</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                    <ChartBarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Analytics Integration</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Task completion metrics</li>
                  <li>• Process performance data</li>
                  <li>• Visual graph representations</li>
                  <li>• Historical task analysis</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                    <HandThumbUpIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Data Persistence</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Server-side task storage</li>
                  <li>• Batch save operations</li>
                  <li>• Data validation</li>
                  <li>• Error handling</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                    <EyeIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Visual Feedback</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Color-coded task types</li>
                  <li>• Status indicators</li>
                  <li>• Progress visualization</li>
                  <li>• Interactive tooltips</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Common Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">No Shifts Available</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Check if staff member has booked shifts</li>
                        <li>• Verify shift dates are in current week</li>
                        <li>• Contact manager to book shifts if needed</li>
                        <li>• Refresh page to reload shift data</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Tasks Not Saving</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Ensure internet connection is stable</li>
                        <li>• Check for server error messages</li>
                        <li>• Verify all required fields are filled</li>
                        <li>• Try saving again after a few moments</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Break Scheduling</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Breaks are required for shifts 6+ hours</li>
                    <li>• Break length is configurable per organization</li>
                    <li>• Additional tasks can be assigned during short breaks</li>
                    <li>• Break times must be within shift hours</li>
                    <li>• System validates break duration requirements</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Assign tasks as early as possible in the planning cycle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Review task assignments regularly for completeness</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use the task graph to monitor performance trends</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Ensure breaks are properly scheduled for long shifts</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Save changes frequently to avoid data loss</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Performance Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Use keyboard shortcuts for faster navigation</li>
                    <li>• Plan task assignments in advance</li>
                    <li>• Review previous tasks for consistency</li>
                    <li>• Monitor completion status regularly</li>
                    <li>• Use mobile view for on-the-go management</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}